// 🔧 components/PricingSection.js
// ✅ Purpose: Reusable pricing section for Landing or other public pages
// ✅ Features:
// - Shows one clear LGV Event Package instead of multiple pricing tiers
// - Shows all core event features included in the standard event package
// - Shows optional revenue add-ons separately
// - Links users to /request_event for event checkout/setup
// - Uses shared LGV feature/add-on constants
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import {
  LGV_EVENT_PACKAGE,
  LGV_CORE_FEATURES,
  LGV_OPTIONAL_POWER_UPS,
} from "../constants/lgv_features";

const PricingSection = () => {
  return (
    <section id="pricing" className="container-fluid py-5 bg-light">
      <div className="container" style={{ maxWidth: "1200px" }}>

        {/* =====================================================
            💵 Section Header
            Purpose:
            - Keeps the pricing model simple and centered around the core event package
            - Makes it clear that add-ons are optional revenue tools
        ===================================================== */}
        <div className="row text-center mb-4">
          <div className="col-12">
            <h4 className="fw-bold">Simple Event Pricing</h4>

            <p className="text-muted mb-0">
              One complete event package for voting and event operations.
              Add optional Power Ups when your event needs them.
            </p>
          </div>
        </div>

        {/* =====================================================
            📦 Core Event Package
            Purpose:
            - Replaces Starter/Professional/Premium pricing tiers
            - Shows the standard LGV package as the main product
        ===================================================== */}
        <div className="card shadow-sm border-success border-3 mb-4">
          <div className="card-body p-4 p-lg-5">
            <div className="row g-4 align-items-center">

              <div className="col-12 col-lg-4">
                <div className="badge bg-success mb-3">
                  Core Offering
                </div>

                <h3 className="fw-bold mb-2">
                  {LGV_EVENT_PACKAGE.name}
                </h3>

                <p className="text-muted">
                  {LGV_EVENT_PACKAGE.description}
                </p>

                <div className="d-flex align-items-end gap-2 mb-3">
                  <div className="display-4 fw-bold">
                    {LGV_EVENT_PACKAGE.priceDisplay}
                  </div>

                  <div className="text-muted mb-2">
                    {LGV_EVENT_PACKAGE.priceNote}
                  </div>
                </div>

                <Link
                  to="/request_event"
                  className="btn btn-success btn-lg w-100"
                >
                  🚀 Start Your Event
                </Link>
              </div>

              <div className="col-12 col-lg-8">
                <h5 className="fw-bold mb-3">
                  Included in every event
                </h5>

                <div className="row g-3">
                  {LGV_CORE_FEATURES.map((feature) => (
                    <div className="col-12 col-md-6" key={feature.key}>
                      <div className="border rounded p-3 h-100 bg-white">
                        <div className="fw-bold mb-1">
                          {feature.icon} {feature.name}
                        </div>

                        <div className="small text-muted">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* =====================================================
            💰 Revenue Add-ons
            Purpose:
            - Shows optional paid tools separately from the core Event Package
            - Reinforces that revenue features are add-ons, not plan tiers
        ===================================================== */}
        <div className="row text-center mb-4">
          <div className="col-12">
            <h4 className="fw-bold">Optional Power Ups</h4>

            <p className="text-muted mb-0">
              Add commerce and promotion tools when your event needs them.
            </p>
          </div>
        </div>

        <div className="row g-4 align-items-stretch">
          {LGV_OPTIONAL_POWER_UPS.map((addOn) => (
            <div className="col-12 col-md-6 col-xl" key={addOn.key}>
              <div
                className="card h-100 shadow-sm border-0"
                style={{
                  borderRadius: "16px",
                  minHeight: "300px",
                }}
              >
                <div className="card-body d-flex flex-column p-4">
                  <div className="display-6 mb-2">
                    {addOn.icon}
                  </div>

                  <h5 className="fw-bold mb-2">
                    {addOn.name}
                  </h5>

                  <div className="fs-3 fw-bold text-success mb-2">
                    {addOn.priceDisplay}
                  </div>

                  <p className="small text-muted mb-4">
                    {addOn.description}
                  </p>

                  <div className="mt-auto">
                    <Link
                      to="/request_event"
                      className="btn btn-outline-success w-100"
                    >
                      Add to Event
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* =====================================================
            🧭 Pricing Footnote
            Purpose:
            - Explains the pricing philosophy clearly
            - Helps customers understand why the package is simple
        ===================================================== */}
        <div className="alert alert-info mt-4 mb-0 text-center">
          <strong>Voting is the core.</strong>{" "}
          Sponsors, volunteers, maps, scheduling, registration, SEO, and reporting
          are included because they help your event run smoothly. Revenue tools are
          optional add-ons because they help your event collect money.
        </div>

      </div>
    </section>
  );
};

export default PricingSection;
